import React, { useState, useEffect, useMemo, useCallback } from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import Accordion from "react-bootstrap/Accordion";
import TaskResultsTable2 from "./TaskResultsTable2.js";
import secondsToTime from "../services/secondsToTime";
import "./ResultsHistory.css";
import DeleteAnswerModal from "./DeleteAnswerModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedo } from "@fortawesome/free-solid-svg-icons";

import { preprocessStudentResults } from "../services/GroupAndAverageTasks";

function AllResultsHistory(props) {
  const {
    studentResults,
    isPrintOut,
    detailedView,
    teacherid,
    schoolid,
    students,
    detailedRef,
    overviewRef,
    hideAccordion, // New prop to control accordion visibility
    viewParent, // New prop to control parent view
    viewTeacher, // New prop to control teacher view
    currentViewRef, // New prop to track the current view
    classDetails, // New prop for class details
  } = props;

  const [assessmentResults, setAssessmentResults] = useState([]);
  const [visibleAccordions, setVisibleAccordions] = useState(new Set());
  const [visibleNestedAccordions, setVisibleNestedAccordions] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalTask, setModalTask] = useState(null);
  const [modalAssessment, setModalAssessment] = useState(null);
  const [answerIds, setAnswerIds] = useState([]);
  const [isRetakeFullAssessment, setIsRetakeFullAssessment] = useState(false);
  const [answerId, setAnswerId] = useState(null);

  const handleModalClose = () => setShowModal(false);

  const YEAR_RANGES = {
    "24/25": { start: "2024-08-15", end: "2025-08-15" },
    "23/24": { start: "2023-08-15", end: "2024-08-15" },
    "22/23": { start: "2022-08-15", end: "2023-08-15" },
  };

  function getCurrentAcademicYear(date = new Date()) {
    const currentTimestamp = date.getTime();

    for (const [label, range] of Object.entries(YEAR_RANGES)) {
      const start = new Date(range.start).getTime();
      const end = new Date(range.end).getTime();

      if (currentTimestamp >= start && currentTimestamp < end) {
        return label;
      }
    }

    return "Unknown Year";
  }

  const academicYear = getCurrentAcademicYear();

  const handleRetakeFullAssessment = (assessment) => {
    const allAnswerIds = assessment.tasks.map((task) => task.answerid);
    setModalTask("All Tasks");
    setModalAssessment(assessment.name);
    setAnswerIds(allAnswerIds);
    setShowModal(true);
    setIsRetakeFullAssessment(true);
  };

  const handleDeleteTask = useCallback(
    (event, assessmentIndex, taskIndex) => {
      event.stopPropagation();
      event.preventDefault();

      const assessment = assessmentResults[assessmentIndex];
      const task = assessment.tasks[taskIndex];
      setModalTask(task?.name);
      setModalAssessment(assessment?.name);
      setAnswerId(task?.answerid);
      setShowModal(true);
      setIsRetakeFullAssessment(false);
    },
    [assessmentResults]
  );

  useEffect(() => {
    // Map over the studentResults to retain its structure
    const updatedStudentResults = Object.keys(studentResults)?.reduce(
      (acc, studentId) => {
        // Find the matching student from the students prop
        const matchingStudent = students?.find(
          (student) => student.id === studentId
        );

        // Add the student name to each assessment
        const assessmentsWithStudentName = studentResults[studentId]?.map(
          (assessment) => ({
            ...assessment,
            studentName: matchingStudent ? matchingStudent.name : "Unknown",
          })
        );

        // Keep the structure the same
        acc[studentId] = assessmentsWithStudentName;
        return acc;
      },
      {}
    );

    // Flatten the updatedStudentResults to get an array of all assessments
    const allAssessments = Object.values(updatedStudentResults).flat();

    setAssessmentResults(preprocessStudentResults(allAssessments));
  }, [studentResults, students]);

  useEffect(() => {
    if (isPrintOut) {
      if (detailedView) {
        // Open all outer and inner accordions for Detailed view
        const allIndexes = new Set(assessmentResults.map((_, index) => index));
        setVisibleAccordions(allIndexes);

        const allNestedAccordions = {};
        assessmentResults.forEach((assessment, assessmentIndex) => {
          assessment.tasks.forEach((_, taskIndex) => {
            const key = `${assessmentIndex}-${taskIndex}`;
            allNestedAccordions[key] = true;
          });
        });
        setVisibleNestedAccordions(allNestedAccordions);
      } else {
        // Open only outer accordions for Simple view
        const allIndexes = new Set(assessmentResults.map((_, index) => index));
        setVisibleAccordions(allIndexes);
        setVisibleNestedAccordions({}); // Close all inner accordions
      }
    } else if (viewParent) {
      // Open the first accordion for all students when viewParent is true
      const firstIndexes = new Set(assessmentResults.map((_, index) => index));
      setVisibleAccordions(firstIndexes);
    } else if (viewTeacher) {
      // Open all accordion items when viewTeacher is true
      const allIndexes = new Set(assessmentResults.map((_, index) => index));
      setVisibleAccordions(allIndexes);
    }
  }, [detailedView, isPrintOut, assessmentResults, viewParent, viewTeacher]);

  useEffect(() => {
    if (viewTeacher) {
      // Open all task accordions for all assessments
      const initialNestedAccordions = {};
      assessmentResults.forEach((assessment, assessmentIndex) => {
        assessment.tasks.forEach((_, taskIndex) => {
          const key = `${assessmentIndex}-${taskIndex}`;
          initialNestedAccordions[key] = true;
        });
      });
      setVisibleNestedAccordions(initialNestedAccordions);
    }
  }, [viewTeacher, assessmentResults]);

  const calculateTotalTime = (groupedTasks) =>
    groupedTasks.reduce((sum, group) => sum + group.aggregated.duration, 0);

  const calculateReadingScore = (assessment) => {
    if (!assessment || !assessment.tasks || assessment.tasks.length === 0) {
      return 0; // Default to 0 if there are no tasks or if the assessment is not defined
    }

    // Calculate the total score directly from `assessment.tasks`.
    const totalScore = assessment.tasks.reduce(
      (sum, task) => sum + task.score,
      0
    );
    const taskCount = assessment.tasks.length;

    // Return the average score across all tasks.
    return Math.round((totalScore / taskCount) * 100);
  };

  // Derive properties like `totalTime` and `readingScore`
  const derivedResults = useMemo(() => {
    return assessmentResults
      .map((assessment) => ({
        ...assessment,
        totalTime: calculateTotalTime(assessment.groupedTasks),
        readingScore: calculateReadingScore(assessment),
      }))
      .filter((assessment) => assessment.studentName !== "Unknown");
  }, [assessmentResults]);

  const taskNames = useMemo(() => {
    const allTaskNames = new Set();
    derivedResults.forEach((assessment) => {
      if (assessment.groupedTasks) {
        assessment.groupedTasks.forEach((task) => {
          allTaskNames.add(task.name);
        });
      }
    });
    return Array.from(allTaskNames);
  }, [derivedResults]);

  const getReadingScoreClass = (score) => {
    if (score === 0) {
      return "low-color";
    } else if (score > 0 && score <= 20) {
      return "low-color";
    } else if (score > 20 && score <= 40) {
      return "med-color";
    } else if (score > 40 && score <= 80) {
      return "noRisk-color";
    } else if (score >= 81 && score <= 100) {
      // Include 81 in high-color
      return "high-color";
    }
    return "";
  };

  const showNestedData = (assessmentIndex) => {
    setVisibleAccordions((prev) => {
      const newVisible = new Set(prev);
      if (newVisible.has(assessmentIndex)) {
        newVisible.delete(assessmentIndex); // Close the clicked accordion
      } else {
        newVisible.add(assessmentIndex); // Open the clicked accordion
      }
      return newVisible; // Return the updated state
    });
  };

  const toggleNestedAccordion = (assessmentIndex, taskIndex) => {
    setVisibleNestedAccordions((prev) => {
      const key = `${assessmentIndex}-${taskIndex}`;
      const newState = { ...prev };
      if (newState[key]) {
        delete newState[key]; // Close the accordion
      } else {
        newState[key] = true; // Open the accordion
      }
      return newState;
    });
  };

  const sortData = (field) => {
    const sortField = field === "atRisk" ? "readingScore" : field;
    setSortCriteria((prev) => {
      const isAscending = prev.field === sortField && prev.order === "asc";
      return { field: sortField, order: isAscending ? "desc" : "asc" };
    });
  };

  const [sortCriteria, setSortCriteria] = useState({
    field: null,
    order: null,
  });

  const renderTaskScore = (groupedTasks, taskName) => {
    const group = groupedTasks.find((t) => t.name === taskName);
    if (!group) return "";

    const score = Math.round(group.aggregated.score * 100);
    let scoreClass = getReadingScoreClass(score);

    return <span className={`task-score ${scoreClass}`}>{score}%</span>;
  };

  const getSortIcon = (fieldName) => {
    if (sortCriteria.field === fieldName) {
      return sortCriteria.order === "asc"
        ? process.env.PUBLIC_URL + "/assets/imgs/desc-icon-active.png"
        : process.env.PUBLIC_URL + "/assets/imgs/asc-icon-active.png";
    }
    return process.env.PUBLIC_URL + "/assets/imgs/asc-icon-default.png";
  };

  const allEventKeys = assessmentResults.flatMap(
    (assessment, assessmentIndex) =>
      assessment.groupedTasks.map(
        (_, groupIndex) => `${assessmentIndex}-${groupIndex}`
      )
  );

  return (
    <div ref={currentViewRef} className="all-results-history-root">
      {" "}
      {/* Wrap the component in a div and assign the ref */}
      <Row>
        <Col>
          <div className="results-history-container">
            <div ref={overviewRef} className="print-only-header">
              <thead className="table-header print-thead table-header-gap">
                <tr>
                  <th
                    colSpan={taskNames.length + 5}
                    className="at-risk-filter-row"
                  >
                    <div className="at-risk-filter">
                      <img
                        src={
                          process.env.PUBLIC_URL + "/assets/imgs/0-20Bar.png"
                        }
                        alt="0-20%"
                        className="risk-bar"
                      />
                      <img
                        src={
                          process.env.PUBLIC_URL + "/assets/imgs/21-40Bar.png"
                        }
                        alt="21-40%"
                        className="risk-bar"
                      />
                    </div>
                  </th>
                </tr>
                <tr>
                  <th
                    className="table-header-cell"
                    onClick={() => sortData("name")}
                  >
                    <div className="header-content">Assessment</div>
                  </th>
                  <th
                    className="table-header-cell"
                    onClick={() => sortData("readingScore")}
                  >
                    <div className="header-content">Reading Score</div>
                  </th>
                  <th
                    className="table-header-cell"
                    onClick={() => sortData("atRisk")}
                  >
                    <div className="header-content">At-Risk</div>
                  </th>
                  {taskNames.map((taskName) => (
                    <th
                      className="table-header-cell"
                      key={taskName}
                      onClick={() => sortData(taskName)}
                    >
                      <div className="header-content">{taskName}</div>
                    </th>
                  ))}
                  <th
                    className="table-header-cell"
                    onClick={() => sortData("totalTime")}
                  >
                    <div className="header-content">Time</div>
                  </th>
                  {!isPrintOut ? (
                    <th className="table-header-cell">
                      <div className="header-content">View</div>
                    </th>
                  ) : null}
                </tr>
              </thead>
            </div>

            <Accordion
              ref={detailedRef}
              className="print-detailed-view"
              defaultActiveKey={
                isPrintOut
                  ? students.map((student) => student.id) // <-- Always open all outer accordions in print (simple or detailed)
                  : viewTeacher
                    ? students.map((student) => student.id)
                    : viewParent && students.length > 0
                      ? [students[0].id]
                      : []
              }
            >
              {students.map((student) => (
                <React.Fragment key={student.id}>
                  {/* Print-only header before each student accordion */}
                  <div className="alpaca-print-header print-only">
                    <div className="printout-logo" />
                    <div className="alpaca-print-header-text">
                      <span className="alpaca-print-header-year">
                        School Year: {academicYear}
                      </span>
                      <span className="alpaca-print-header-year">
                        Student Name: {student.name}
                      </span>
                      <span className="alpaca-print-header-powered">
                        Powered by ALPACA &copy; {new Date().getFullYear()}
                      </span>
                    </div>
                  </div>
                  <div
                    className="student-print-block break-after"
                    key={student.id}
                  >
                    <Accordion.Item
                      eventKey={student.id}
                      key={student.id}
                      className="avoid-page-break"
                    >
                      <Accordion.Header className="avoid-page-break">
                        {student.name}
                      </Accordion.Header>
                      <Accordion.Body className="avoid-page-break">
                        <Table className="result-history-table">
                          <thead
                            className="table-header print-thead avoid-page-break table-header-gap"
                            style={{ marginBottom: "16px" }}
                          >
                            <tr className="table-row avoid-page-break">
                              <th
                                className="table-header-cell name-column"
                                onClick={() => sortData("name")}
                              >
                                <div>Screener</div>
                              </th>
                              <th
                                className="table-header-cell"
                                onClick={() => sortData("readingScore")}
                              >
                                <div className="header-content">
                                  <span>Reading Score</span>
                                  <img
                                    className="sort-icon"
                                    src={
                                      process.env.PUBLIC_URL +
                                      "/assets/imgs/chevron-sort.svg"
                                    }
                                    alt="Sort"
                                  />
                                </div>
                              </th>

                              {taskNames.map((taskName) => (
                                <th
                                  className="table-header-cell"
                                  key={taskName}
                                  onClick={() => sortData(taskName)}
                                >
                                  <div className="header-content">
                                    <span>{taskName}</span>
                                    <img
                                      className="sort-icon"
                                      src={
                                        process.env.PUBLIC_URL +
                                        "/assets/imgs/chevron-sort.svg"
                                      }
                                      alt="Sort"
                                    />
                                  </div>
                                </th>
                              ))}
                              <th
                                className="table-header-cell"
                                onClick={() => sortData("atRisk")}
                              >
                                <div className="header-content">
                                  <span>At-Risk</span>
                                  <img
                                    className="sort-icon"
                                    src={
                                      process.env.PUBLIC_URL +
                                      "/assets/imgs/chevron-sort.svg"
                                    }
                                    alt="Sort"
                                  />
                                </div>
                              </th>
                              <th
                                className="table-header-cell"
                                onClick={() => sortData("totalTime")}
                              >
                                <div className="header-content">
                                  <spam>Time</spam>
                                  <img
                                    className="sort-icon"
                                    src={
                                      process.env.PUBLIC_URL +
                                      "/assets/imgs/chevron-sort.svg"
                                    }
                                    alt="Sort"
                                  />
                                </div>
                              </th>
                              {!isPrintOut ? (
                                <th className="table-header-cell">
                                  <div className="header-content">View</div>
                                </th>
                              ) : null}
                            </tr>
                          </thead>
                          <tbody className="result-history-table-rows avoid-page-break table-body-gap">
                            {derivedResults
                              .filter(
                                (assessment) =>
                                  assessment.studentName === student.name
                              )
                              .map((assessment, assessmentIndex) => (
                                <React.Fragment key={assessmentIndex}>
                                  <tr
                                    id={assessment.studentName
                                      .replace(/\s+/g, "-")
                                      .toLowerCase()}
                                    className="table-row avoid-page-break"
                                  >
                                    <td
                                      className="name-column report-cards-text all-results-history-td"
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      {assessment.name}
                                    </td>
                                    <td className="centre-item task-badge-parent all-results-history-td">
                                      {assessment.readingScore ? (
                                        <span
                                          className={`reading-score ${getReadingScoreClass(
                                            assessment.readingScore
                                          )}`}
                                        >
                                          {assessment.readingScore}%
                                        </span>
                                      ) : null}
                                      <span className="tooltip">
                                        Average of all screener reading scores
                                        this year
                                      </span>
                                    </td>

                                    {taskNames.map((taskName) => (
                                      <td
                                        className="results-task-score centre-item task-badge-parent all-results-history-td"
                                        data-value={taskName
                                          .replace(/\s+/g, "-")
                                          .toLowerCase()}
                                        key={taskName}
                                      >
                                        {renderTaskScore(
                                          assessment.groupedTasks,
                                          taskName
                                        )}
                                      </td>
                                    ))}
                                    <td className="all-results-history-td">
                                      {assessment.readingScore !== undefined &&
                                        assessment.readingScore < 20 ? (
                                        <img
                                          src={
                                            process.env.PUBLIC_URL +
                                            "/assets/imgs/risk-flag-red.png"
                                          }
                                          className="flag-size-results"
                                          alt="Red Flag"
                                        />
                                      ) : assessment.readingScore >= 20 &&
                                        assessment.readingScore <= 40 ? (
                                        <img
                                          src={
                                            process.env.PUBLIC_URL +
                                            "/assets/imgs/risk-flag-yellow.png"
                                          }
                                          className="flag-size-results"
                                          alt="Yellow Flag"
                                        />
                                      ) : null}
                                    </td>
                                    <td
                                      className="results-task-score centre-item all-results-history-td"
                                      data-value={assessment.name}
                                    >
                                      {secondsToTime(assessment.totalTime)}
                                    </td>
                                    {!isPrintOut ? (
                                      <td
                                        className="all-results-history-td"
                                        data-toggle="toggle"
                                      >
                                        <img
                                          className="collapse-click"
                                          onClick={() =>
                                            showNestedData(assessmentIndex)
                                          }
                                          src={
                                            visibleAccordions.has(
                                              assessmentIndex
                                            )
                                              ? "../assets/imgs/minus-icon-small.png"
                                              : "../assets/imgs/plus-icon-small.png"
                                          }
                                        />
                                      </td>
                                    ) : null}
                                  </tr>
                                  {!hideAccordion && detailedView && (
                                    <tr
                                      colSpan="12"
                                      id={
                                        assessment.name
                                          .replace(/\s+/g, "-")
                                          .toLowerCase() + "-data"
                                      }
                                      className={`result-accordian-width ${visibleAccordions.has(
                                        assessmentIndex
                                      ) || isPrintOut
                                        ? ""
                                        : "hide"
                                        } avoid-page-break`}
                                    >
                                      <td
                                        colSpan="12"
                                        className="avoid-page-break"
                                      >
                                        <Row>
                                          <Col className="text-left">
                                            {!isPrintOut && (
                                              <div className="retake-full-assessment-button-container">
                                                <button
                                                  className="gold-button"
                                                  onClick={() =>
                                                    handleRetakeFullAssessment(
                                                      assessment
                                                    )
                                                  }
                                                >
                                                  Retake{" "}
                                                  {assessment.studentName}
                                                </button>
                                              </div>
                                            )}
                                          </Col>
                                        </Row>
                                        <Accordion
                                          className="results-accordion avoid-page-break"
                                          activeKey={assessment.tasks
                                            .map(
                                              (_, taskIndex) =>
                                                `${assessmentIndex}-${taskIndex}`
                                            )
                                            .filter(
                                              (key) =>
                                                visibleNestedAccordions[key]
                                            )}
                                        >
                                          {assessment.tasks.map(
                                            (task, taskIndex) => (
                                              <Accordion.Item
                                                eventKey={`${assessmentIndex}-${taskIndex}`}
                                                key={taskIndex}
                                                style={{ border: "white" }}
                                                className="avoid-page-break"
                                              >
                                                <Accordion.Header
                                                  onClick={() =>
                                                    toggleNestedAccordion(
                                                      assessmentIndex,
                                                      taskIndex
                                                    )
                                                  }
                                                  className="report-card-accordion-head avoid-page-break"
                                                >
                                                  <Row
                                                    className="task-results-table-row avoid-page-break"
                                                    style={{
                                                      alignItems: "center",
                                                    }}
                                                  >
                                                    <Col
                                                      // className="col-10 align-left"
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                      }}
                                                    >
                                                      <button
                                                        className="delete-button"
                                                        style={{
                                                          marginRight: 16,
                                                        }}
                                                        onClick={(e) =>
                                                          handleDeleteTask(
                                                            e,
                                                            assessmentIndex,
                                                            taskIndex
                                                          )
                                                        }
                                                      >
                                                        <FontAwesomeIcon
                                                          icon={faRedo}
                                                        />
                                                      </button>
                                                    </Col>
                                                    <Col>
                                                      <span
                                                        style={{
                                                          whiteSpace: "nowrap",
                                                          // overflow: "hidden",

                                                          display:
                                                            "inline-block",
                                                          width: "100%",
                                                        }}
                                                      >
                                                        <i>
                                                          {task.name} (
                                                          {task.parentTaskName ||
                                                            task.name}
                                                          )
                                                        </i>
                                                      </span>
                                                    </Col>
                                                  </Row>
                                                </Accordion.Header>
                                                <Accordion.Body className="avoid-page-break">
                                                  <TaskResultsTable2
                                                    taskAnswers={task.answers}
                                                  />
                                                </Accordion.Body>
                                              </Accordion.Item>
                                            )
                                          )}
                                        </Accordion>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                          </tbody>
                        </Table>
                      </Accordion.Body>
                    </Accordion.Item>
                  </div>
                </React.Fragment>
              ))}
            </Accordion>
          </div>
        </Col>
      </Row>
      <DeleteAnswerModal
        show={showModal}
        handleClose={handleModalClose}
        taskName={modalTask}
        studentName={modalAssessment}
        assessmentName={modalAssessment}
        handleDelete={() => { }}
        answerIds={answerIds}
        teacherid={teacherid}
        isRetakeFullAssessment={isRetakeFullAssessment}
      />
    </div>
  );
}

export default AllResultsHistory;
